import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationsService } from '@core/services/notifications.service';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { ServiceConsentRoutingEnum } from '@features/services/service-consents-page-wrapper/service-consents-routing.enum';
import { transformRouterLinkSegmentsIntoRelativeUrl } from '@global/utils/utils';
import { ProfileServieAuthorization } from '@loyalty-v3/libs';
import { NotificationType } from '@models/notification/notification';
import { Actions, ROOT_EFFECTS_INIT, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { partnerId } from '@stores/partner/partner.selectors';
import { addToServicesAuthorizations } from '@stores/profile/profile.actions';
import { selectProfile, servicesAuthorizations } from '@stores/profile/profile.selectors';
import { isServiceUnavailableInUserPlan } from '@stores/selectors/profile-services.selectors';
import {
  activateService,
  endConsentRedirect,
  init,
  redirectAfterConsentsCheck,
  saveServiceConsents,
  serviceAlreadyActivated,
  serviceNotActivated,
} from '@stores/services/services.actions';
import {
  SERVICE_REDIRECT_URL_QUERY_PARAM_KEY,
  areConsentsRequired,
  getServices,
} from '@stores/services/services.utils';
import { selectIfDefined } from '@stores/utils/selects.operators';
import { ServicesV3WebService } from '@webservices/services/services.webservice';
import { TcsDataModel, TcsWebservice } from '@webservices/tcs/tcs.webservice';
import { isLogged } from '@wizbii-utils/angular/stores';
import { EMPTY, catchError, combineLatest, filter, map, of, switchMap, take, tap } from 'rxjs';

export const loadProfile = createEffect(
  (actions$ = inject(Actions), servicesWebservice = inject(ServicesV3WebService), store = inject(Store)) =>
    actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      switchMap(() => store.select(isLogged)),
      filter((isLogged) => isLogged),
      switchMap(() => servicesWebservice.getServices()),
      switchMap((services) => getServices(services, store)),
      map((state) => init({ state }))
    ),
  { functional: true }
);

const toTcsDataModel = ({
  userId,
  platformId,
  serviceId,
}: {
  userId: string;
  platformId: string;
  serviceId: string;
}): TcsDataModel => ({
  provider: 'obendy',
  data: {
    event: 'activate_service',
    platformId,
    userId,
    serviceId,
  },
});

/**
 * @deprecated
 * See {@link activateService} */
export const activateServiceEffect = createEffect(
  (actions$ = inject(Actions), tcsWebservice = inject(TcsWebservice), store = inject(Store)) =>
    actions$.pipe(
      ofType(activateService),
      switchMap(({ serviceId, mustRedirect, queryParams, targetRoutePath }) =>
        store.select(isLogged).pipe(
          filter((isLogged) => isLogged),
          switchMap(() =>
            combineLatest([
              store.select(servicesAuthorizations),
              areConsentsRequired(serviceId, store),
              selectIfDefined(store, isServiceUnavailableInUserPlan(serviceId)),
            ])
          ),
          filter(
            (combined): combined is [ProfileServieAuthorization[], boolean, boolean] =>
              !!combined && !!combined[0] && combined[2] !== undefined
          ),
          take(1),
          switchMap(([servicesAuthorizations, areConsentsRequired, hasNotAccess]) => {
            if (servicesAuthorizations.find((authorization) => authorization.applicationId === serviceId))
              return of(serviceAlreadyActivated());
            if (areConsentsRequired) return of(serviceNotActivated({ message: 'Consents are required' }));
            if (hasNotAccess) return of(serviceNotActivated({ message: 'Service is not available in current plan' }));

            return combineLatest([selectIfDefined(store, selectProfile), selectIfDefined(store, partnerId)]).pipe(
              take(1),
              map(([profile, id]) => toTcsDataModel({ userId: profile.id, platformId: id, serviceId })),
              switchMap((payload) => tcsWebservice.send(payload)),
              map(() => addToServicesAuthorizations({ serviceId, mustRedirect, queryParams, targetRoutePath }))
            );
          })
        )
      )
    ),
  { functional: true }
);

/**
 * @deprecated
 * See {@link saveServiceConsents} */
export const saveServiceConsentsEffect = createEffect(
  (
    actions$ = inject(Actions),
    servicesWebservice = inject(ServicesV3WebService),
    store = inject(Store),
    notificationsService = inject(NotificationsService)
  ) =>
    actions$.pipe(
      ofType(saveServiceConsents),
      switchMap(({ serviceId, userConsents }) =>
        store.select(isLogged).pipe(
          filter((isLogged) => isLogged),
          switchMap(() => servicesWebservice.saveServiceConsents(serviceId, userConsents)),
          map(() => addToServicesAuthorizations({ serviceId })),
          catchError(() => {
            notificationsService.createNotification({
              title: "Une erreur s'est produite",
              text: 'Veuillez contacter le support',
              type: NotificationType.danger,
              date: Date.now(),
            });
            return EMPTY;
          })
        )
      )
    ),
  { functional: true }
);

/**
 * @deprecated
 * See {@link redirectAfterConsentsCheck} */
export const redirectAfterConsentsCheckEffect = createEffect(
  (actions$ = inject(Actions), router = inject(Router), store = inject(Store)) =>
    actions$.pipe(
      ofType(redirectAfterConsentsCheck),
      switchMap(({ serviceId, targetRoutePath, queryParams, mustActivate }) =>
        store.select(isLogged).pipe(
          filter((isLogged) => isLogged),
          switchMap(() => combineLatest([store.select(servicesAuthorizations), areConsentsRequired(serviceId, store)])),
          take(1),
          map(([servicesAuthorizations, areConsentsRequired]) => {
            const serviceIsAuthorized = servicesAuthorizations?.find(
              (authorization) => authorization.applicationId === serviceId
            );

            if (!areConsentsRequired && !serviceIsAuthorized && mustActivate) {
              return activateService({ serviceId, mustRedirect: true, queryParams, targetRoutePath });
            }

            if (serviceIsAuthorized || !areConsentsRequired) {
              router.navigate(targetRoutePath, {
                queryParams,
                queryParamsHandling: queryParams ? 'merge' : 'preserve',
              });

              return endConsentRedirect();
            }

            const newQueryParams = {
              ...queryParams,
              ...(targetRoutePath.length > 0 && {
                [SERVICE_REDIRECT_URL_QUERY_PARAM_KEY]: transformRouterLinkSegmentsIntoRelativeUrl(targetRoutePath),
              }),
            };

            router.navigate(['/', FeaturesRoutingEnum.Services, serviceId, ServiceConsentRoutingEnum.Consent], {
              queryParams: newQueryParams,
              queryParamsHandling: 'merge',
            });

            return endConsentRedirect();
          })
        )
      )
    ),
  { functional: true, dispatch: true }
);

/**
 * @deprecated
 * See {@link addToServicesAuthorizations} */
export const addToServicesAuthorizationsEffect = createEffect(
  (actions$ = inject(Actions), router = inject(Router), store = inject(Store)) =>
    actions$.pipe(
      ofType(addToServicesAuthorizations),
      switchMap(({ serviceId, mustRedirect, queryParams, targetRoutePath }) =>
        store.select(isLogged).pipe(
          filter((isLogged) => isLogged),
          switchMap(() => combineLatest([store.select(servicesAuthorizations), areConsentsRequired(serviceId, store)])),
          take(1),
          tap(([servicesAuthorizations, areConsentsRequired]) => {
            if (mustRedirect && targetRoutePath) {
              if (
                servicesAuthorizations?.find((authorization) => authorization.applicationId === serviceId) ||
                !areConsentsRequired
              ) {
                router.navigate(targetRoutePath, {
                  queryParams,
                  queryParamsHandling: queryParams ? 'merge' : 'preserve',
                });
              }
            }
          })
        )
      )
    ),
  { functional: true, dispatch: false }
);
